<template>
    <hs-modal :model-value="isOpen" :title="isCreating ? `Add ${itemTypeLabel}` : `Edit ${itemTypeLabel}`" :description="activeTab === 'products'
        ? 'Choose the product packaging unit used in inventory and transfers.'
        : 'Manage service details and searchable service SKU.'" size="lg" @update:modelValue="handleModalModel">
        <form class="catalog-form" @submit.prevent="handleSubmit">
            <div class="catalog-form-grid">
                <hs-input v-model="formData.name" label="Name" icon="tag" placeholder="Enter name" required />

                <div v-if="activeTab === 'services'">
                    <hs-textarea v-model="formData.description" label="Description"
                        placeholder="Short service description" rows="4" />
                </div>

                <div class="catalog-form-row">
                    <hs-input v-model="formData.sku" label="SKU" icon="hash" placeholder="Generate or enter SKU" />
                    <hs-button type="button" variant="secondary" icon="refresh-cw" @click="generateSku">
                        Generate
                    </hs-button>
                </div>

                <div v-if="activeTab === 'products'" class="catalog-form-row">
                    <hs-input v-model="formData.barcode" label="Barcode" icon="maximize"
                        placeholder="Generate or enter barcode" />
                    <hs-button type="button" variant="secondary" icon="refresh-cw" @click="generateBarcode">
                        Generate
                    </hs-button>
                </div>

                <div v-if="activeTab === 'products'">
                    <hs-select v-model="formData.quantity_unit" :options="quantityUnitOptions" label="Quantity Unit"
                        icon="layers" :disabled="loadingUnits" placeholder="Choose a unit" />
                </div>
            </div>

            <div class="catalog-form-actions">
                <hs-button type="button" variant="secondary" @click="$emit('close')">
                    Cancel
                </hs-button>
                <hs-button type="submit" icon="check" :loading="isSaving" :disabled="isSaving">
                    {{ saveButtonLabel }}
                </hs-button>
            </div>
        </form>
    </hs-modal>
</template>

<script>
import http from "@/helper/http";
import { HsButton, HsInput, HsModal, HsSelect, HsTextarea } from "@/components/ui";

export default {
    name: "EditItemModal",
    components: {
        HsButton,
        HsInput,
        HsModal,
        HsSelect,
        HsTextarea,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        activeTab: {
            type: String,
        },
        item: {
            type: Object,
            default: () => ({}),
        },
        loadingModifyProduct: {
            type: Boolean,
            default: false,
        },
        loadingModifyService: {
            type: Boolean,
            default: false,
        },
        isCreating: {
            type: Boolean,
            default: false,
        },
        storeId: {
            type: [String, Number],
            default: "",
        },
    },
    data() {
        return {
            formData: {
                name: "",
                sku: "",
                barcode: "",
                description: "",
                quantity_unit: "U",
            },
            quantityUnits: [],
            loadingUnits: false,
        };
    },
    computed: {
        itemTypeLabel() {
            return this.activeTab === "products" || this.activeTab === "inventory"
                ? "Product"
                : "Service";
        },
        isSaving() {
            return this.loadingModifyProduct || this.loadingModifyService;
        },
        saveButtonLabel() {
            return this.isCreating ? `Create ${this.itemTypeLabel}` : `Save ${this.itemTypeLabel}`;
        },
        quantityUnitOptions() {
            return this.quantityUnits.map((unit) => ({
                label: `${unit.name || unit.label || unit.unit} (${unit.unit})`,
                value: unit.unit,
            }));
        },
    },
    watch: {
        item: {
            immediate: true,
            handler(newItem) {
                const item = newItem || {};
                this.formData = {
                    name: item.name || "",
                    sku: item.item_sku || item.sku || "",
                    barcode: item.barcode || "",
                    description: item.description || "",
                    quantity_unit: item.quantity_unit || item.default_quantity_unit || "U",
                };
            },
        },
        isOpen: {
            immediate: true,
            handler(val) {
                if (val && this.activeTab === "products") {
                    this.loadQuantityUnits();
                }
            },
        },
        activeTab(val) {
            if (val === "products" && this.isOpen) {
                this.loadQuantityUnits();
            }
        },
        storeId() {
            if (this.isOpen && this.activeTab === "products") {
                this.loadQuantityUnits();
            }
        },
    },
    methods: {
        async loadQuantityUnits() {
            this.loadingUnits = true;
            try {
                const response = await http.get("/v1/store/inventory/quantity/units", {
                    params: this.storeId ? { store_id: this.storeId } : {},
                });
                this.quantityUnits = Array.isArray(response?.data) ? response.data : [];
            } catch (error) {
                console.error("Failed to load quantity units", error);
                this.quantityUnits = [
                    { unit: "U", name: "Pieces/item", description: "Pieces/item (Number)" },
                ];
            } finally {
                this.loadingUnits = false;
            }
        },
        handleModalModel(open) {
            if (!open) {
                this.$emit("close");
            }
        },
        handleSubmit() {
            this.$emit("save", this.buildPayload());
        },
        buildPayload() {
            const item = this.item || {};
            if (this.activeTab === "services") {
                const payload = {
                    service_id: item.id,
                    name: this.formData.name,
                    description: this.formData.description,
                    item_sku: this.formData.sku,
                };
                if (this.isCreating) {
                    delete payload.service_id;
                    payload.store_id = this.$store.getters.getSelectedStore;
                }
                return payload;
            }

            const payload = {
                barcode: this.formData.barcode,
                product_id: item.productId,
                name: this.formData.name,
                item_sku: this.formData.sku,
                default_quantity_unit: this.formData.quantity_unit || "U",
            };
            if (this.isCreating) {
                delete payload.product_id;
                payload.store_id = this.$store.getters.getSelectedStore;
            }
            return payload;
        },
        generateSku() {
            if (!this.formData.name || this.formData.name.length < 2) return;
            const randomSixDigits = Math.floor(100000 + Math.random() * 900000).toString();
            const lastTwoChars = this.formData.name.slice(-2).toUpperCase();
            const skuArray = randomSixDigits.split("");
            skuArray.splice(1, 0, lastTwoChars[0]);
            skuArray.splice(skuArray.length - 1, 0, lastTwoChars[1]);
            this.formData.sku = skuArray.join("");
        },
        generateBarcode() {
            this.formData.barcode = Math.floor(1000000000000 + Math.random() * 9000000000000).toString();
        },
    },
};
</script>

<style scoped>
.catalog-form {
    display: grid;
    gap: 1.25rem;
}

.catalog-form-grid {
    display: grid;
    gap: 1rem;
}

.catalog-form-row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 0.75rem;
    align-items: flex-end;
}

.catalog-form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1rem;
}

@media (max-width: 640px) {
    .catalog-form-row {
        grid-template-columns: 1fr;
    }
}
</style>
